import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, UncontrolledTooltip } from "reactstrap";
import { InputSwitch } from "primereact/inputswitch";
import {
    Columns,
    LazyTableState,
} from "src/models/components/dataTableWrapperModel";
import DataTableWrapper from "src/components/Grid/DataTableWrapper";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "src/components/Common/ReactToaster";
import FeatherIcon from "feather-icons-react";
import FilterIcon from "src/components/Common/CustomFilterandSortIcon/FilterIcon";
import SortIcon from "src/components/Common/CustomFilterandSortIcon/SortIcon";
import { RootReducerState } from "src/store/reducers";
import {
    getCourierTypeReportList,
} from "src/store/courierReport/actions";
import { getEmployeeDropdownList } from "src/store/dealerLastUpdated/actions";
import { formatDateMEL, formatDateTimeMEL } from "src/helpers/common_helpers";
import CustomizeTable from "src/components/Common/CustomizeTable/CustomizeTable";
import { customizeTableColumns } from "src/models/components/CustomizeTableColumns";
import { saveAsExcelFile } from "src/helpers/common_helpers";
import { API } from "src/util/api";
import ExportFile from "src/components/Common/ExcelPdf Export/ExportFile";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

const courierTypeReportData = (data: any) => {
    if (!data) return [];
    return data.map((item: any) => ({
        ...item,
        JobConveterGuid: item.employeeName ? item.employeeName : "",
    }));
};

export default function CourierTypeReports() {
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const dt = useRef<DataTable<any>>(null);

    // Selectors
    const { courierTypeReportList, loading, error } = useSelector(
        (state: RootReducerState) => state.CourierTypeReportReducer
    );
    const { values, totalRecords } = courierTypeReportList;

    const modifiedValues = useMemo(() => {
        return courierTypeReportData(values);
    }, [values]);

    const { profile } = useSelector((state: RootReducerState) => state.profilesReducer);

    const { employeeDropdownList } = useSelector(
        (state: RootReducerState) => state.DealerLastUpdatedReducer
    );
    let currentUserId = JSON.parse(localStorage.getItem('authUser') || "{}")?.uid
    // State
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showCustomizeTable, setShowCustomizeTable] = useState(false);
    const [pdfLoading, setPdfLoading] = useState<boolean>(false);
    const [showMyJob, setShowMyJob] = useState(false);

    // Filter details
    const filterDetails: DataTableFilterMeta = {
        companyName: { value: "", matchMode: "contains" },
        awbNumber: { value: "", matchMode: "contains" },
        jobNumber: { value: "", matchMode: "contains" },
        status: { value: "", matchMode: "contains" },
        courierType: { value: "", matchMode: "contains" },
        CreationStartDate: { value: null, matchMode: "equals" },
        CreationEndDate: { value: null, matchMode: "equals" },
        creationTime: { value: null, matchMode: "contains" }, // For the UI filter input
        srn: { value: null, matchMode: "contains" },
        JobConveterGuid: { value: "", matchMode: "equals" },
    };

    // Initial lazy state
    const initialLazyState: LazyTableState = {
        first: 0,
        rows: 10,
        page: 1,
        sortField: "creationTime",
        sortOrder: -1,
        filters: filterDetails,
    };

    const [lazyState, setLazyState] = useState(initialLazyState);

    const employeeOptions = useMemo(() => {
        return employeeDropdownList?.map((emp: any) => ({
            label: emp.name,
            value: emp.userId,
        }));
    }, [employeeDropdownList]);

    const employeeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                options={employeeOptions}
                name="employeeFilter"
                optionLabel="label"
                onChange={(e: DropdownChangeEvent) => {
                    options.filterCallback(e.value, options.index);
                    return {};
                }}
                required={true}
                value={options.value}
                filter
                itemTemplate={(option: any) => (
                    <div
                        title={option.label}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {option.label}
                    </div>
                )}
            />
        );
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (<>
            <label htmlFor="">Start Date</label>
            <Input type="date"
                required
                name={options?.value?.startDate}
                value={options?.value?.startDate || ""}
                max={options?.value?.endDate ? options?.value?.endDate : ""}
                onChange={(e: any) => {
                    let modifiedDate: any = options.value || { startDate: "", endDate: "" };
                    modifiedDate['startDate'] = e.target.value;
                    options.filterCallback(modifiedDate, options.index);
                }}
            />
            <br />
            <label htmlFor="">End Date</label>
            <Input type="date"
                required
                name={options?.value?.endDate}
                value={options?.value?.endDate || ""}
                min={options?.value?.startDate ? options?.value?.startDate : ""}
                onChange={(e: any) => {
                    let modifiedDate: any = options.value || { startDate: "", endDate: "" };
                    modifiedDate['endDate'] = e.target.value;
                    options.filterCallback(modifiedDate, options.index);
                }}
            />
        </>);
    };

    // Columns definition
    const columns: Columns[] = [
        {
            field: "companyName",
            sortable: true,
            header: "Company Name",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "150px" },
            isDefault: true,
            isStatic: true,
        },
        {
            field: "JobConveterGuid",
            sortable: false,
            header: "Employee Name",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "200px" },
            isDefault: true,
            isStatic: false,
            filterElement: employeeFilterTemplate
        },
        {
            field: "awbNumber",
            sortable: true,
            header: "AWB Number",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "150px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "jobNumber",
            sortable: true,
            header: "Job Number",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "150px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "srn",
            sortable: true,
            header: "SR Number",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "150px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "status",
            sortable: true,
            header: "Status",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "120px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "courierType",
            sortable: true,
            header: "Courier Type",
            filter: true,
            showFilterMenu: true,
            headerStyle: { whiteSpace: "nowrap", minWidth: "120px" },
            isDefault: true,
            isStatic: false,
        },
        {
            field: "creationTime",
            sortable: true,
            header: "Creation Time",
            filter: true,
            showFilterMenu: true,
            body: (rowData: any) => formatDateMEL(rowData.creationTime),
            headerStyle: { whiteSpace: "nowrap", minWidth: "180px" },
            isDefault: true,
            isStatic: false,
            filterElement: dateFilterTemplate
        },
        {
            field: "tatDisplay",
            sortable: false,
            header: "TAT",
            filter: false,
            showFilterMenu: false,
            headerStyle: { whiteSpace: "nowrap", minWidth: "120px" },
            isDefault: true,
            isStatic: false,
        },
    ];

    const [visibleColumns, setVisibleColumns] = useState(
        columns.filter((eachColumn: Columns) => eachColumn.isDefault)
    );

    useEffect(() => {
        setVisibleColumns(columns.filter((eachColumn: Columns) => eachColumn.isDefault))
    }, [employeeDropdownList])

    // Effects
    useEffect(() => {
        dispatch(getEmployeeDropdownList());
    }, []);

    useEffect(() => {
        if (error) {
            showToast(error, { type: "error" });
        }
    }, [error]);

    useEffect(() => {
        callbackFunction(lazyState);
    }, [lazyState, profile]);

    useEffect(() => {
        setLazyState((prev: any) => ({
            ...prev,
            filters: {
                ...prev.filters,
                JobConveterGuid: { value: showMyJob ? currentUserId : "", matchMode: "equals" }
            }
        }));
    }, [showMyJob]);

    useEffect(() => {
        const preloader: any = document.getElementById("preloader");
        if (loading || pdfLoading) {
            preloader.style.display = "block";
        } else {
            preloader.style.display = "none";
        }
    }, [loading, pdfLoading]);

    // Callbacks
    const callbackFunction = (lazyState: LazyTableState) => {
        let modifiedLazyState = {
            ...lazyState,
            filters: {
                ...lazyState.filters,
                creationTime: { value: "" },
            }
        }
        dispatch(getCourierTypeReportList(modifiedLazyState));
    };

    // Handlers
    const onPage = (event: any) => {
        setLazyState(event);
    };

    const onSort = (event: any) => {
        setLazyState(event);
    };

    const onFilter = (event: any) => {
        event["first"] = 0;
        // Map creationTime date range to CreationStartDate and CreationEndDate
        event['filters']['CreationStartDate']['value'] = event?.filters?.creationTime?.value?.startDate ? event?.filters?.creationTime?.value?.startDate : '';
        event['filters']['CreationEndDate']['value'] = event?.filters?.creationTime?.value?.endDate ? event?.filters?.creationTime?.value?.endDate : '';
        setLazyState(event);
    };

    const clearFilter = () => {
        setShowMyJob(false);
        setLazyState(initialLazyState);
    };

    const customizeTableClickHandler = () => {
        setShowCustomizeTable(true);
    };

    const customizeTableCancelHandler = () => {
        setShowCustomizeTable(false);
    };

    const customizeColumnSubmitHandler = (customizeColumnObject: any[]) => {
        let orderedSelectedColumns = columns.filter((col) =>
            customizeColumnObject.some((sCol: any) => sCol === col.field)
        );
        setVisibleColumns(orderedSelectedColumns);
        setShowCustomizeTable(false);
    };

    const toggleTooltip = () => setTooltipOpen((prev) => !prev);

    // Export functions
    const changeColumnDetails = (values: any[]) => {
        return values?.map((eachItem) => {
            return {
                "Company Name": eachItem.companyName,
                "AWB Number": eachItem.awbNumber,
                "Job Number": eachItem.jobNumber,
                "SR Number": eachItem.srn,
                "Status": eachItem.status,
                "Courier Type": eachItem.courierType,
                "Creation Time": eachItem.creationTime ? formatDateTimeMEL(eachItem.creationTime) : "",
                "TAT": eachItem.tatDisplay,
            };
        });
    };

    const saveDataAsExcel = (values: any[], fileName: string) => {
        const modifiedColumnDetails = changeColumnDetails(values);
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(modifiedColumnDetails);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });
            saveAsExcelFile(excelBuffer, fileName);
        });
    };

    const saveDataAsPdf = (values: any[], columns: any[], fileName: string) => {
        const destructuredData = values?.map((data) => ({
            companyName: data.companyName,
            awbNumber: data.awbNumber,
            jobNumber: data.jobNumber,
            status: data.status,
            courierType: data.courierType,
            creationTime: data.creationTime ? formatDateTimeMEL(data.creationTime) : "",
            tatDisplay: data.tatDisplay,
        }));

        import("jspdf").then((jsPDF) => {
            import("jspdf-autotable").then(() => {
                const exportColumns = columns?.map((col) => ({
                    title: col.header,
                    dataKey: col.field,
                    styles: {
                        cellPadding: 2,
                        halign: "center",
                    },
                }));
                const doc = new (jsPDF as any).default({
                    orientation: "landscape",
                    format: "a4",
                });
                doc.autoTable(exportColumns, destructuredData);
                doc.save(`${fileName}.pdf`);
            });
        });
    };

    const fetchData = async (isPdf: boolean) => {
        let state: any = {
            ...lazyState,
            rows: totalRecords > 5000 ? 5000 : totalRecords < 1 ? 1 : totalRecords,
            first: 0,
        };

        try {
            setPdfLoading(true);
            const response = await API.getCourierTypeReportAPI(state);
            if (!response) {
                throw new Error("Network response was not ok");
            }
            const resp = response?.data?.items;
            if (isPdf) {
                saveDataAsPdf(resp, columns, "courier-type-reports");
            } else {
                saveDataAsExcel(resp, "courier-type-reports");
            }
            setPdfLoading(false);
        } catch (error: any) {
            showToast(error?.message, { type: "error" });
            setPdfLoading(false);
        }
    };

    const csvClickHandler = () => {
        fetchData(false);
    };

    const pdfClickHandler = () => {
        fetchData(true);
    };

    // Customize table columns data
    const customizeTableColumnsData: customizeTableColumns[] = columns.map(
        (eachColumns: Columns) => {
            return {
                field: eachColumns.field,
                value: visibleColumns.some((sCol: any) => sCol.field === eachColumns.field),
                isDisable: eachColumns.isStatic ?? false,
                header: eachColumns.header,
            };
        }
    );

    // Header
    const header = (
        <div className="table-header-container">
            <div>Courier Type Reports</div>
            <div className="flex-grow-1"></div>
            <div className="table-header-container-item">
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div className='me-1 d-flex gap-1 align-items-center'>
                        <div>Show My Job</div> &nbsp;
                        <InputSwitch checked={showMyJob} onChange={(e) => setShowMyJob(e.value)} />
                    </div>
                    <ExportFile
                        exportExcel={csvClickHandler}
                        exportPdf={pdfClickHandler}
                        clearFilter={clearFilter}
                        isFilter={true}
                    />
                    <Button
                        type="button"
                        className="customize-table-button"
                        id="ScheduleUpdateTooltip"
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                        data-btn="btn"
                        onClick={customizeTableClickHandler}
                    >
                        <FeatherIcon icon="sliders" className="" />
                    </Button>
                </div>
                <UncontrolledTooltip
                    placement="bottom"
                    target="ScheduleUpdateTooltip"
                    isOpen={tooltipOpen}
                    toggle={toggleTooltip}
                    innerClassName="tooltip-color"
                >
                    Choose fields you want to see in the table
                </UncontrolledTooltip>
            </div>
        </div>
    );

    return (
        <>
            <div className="page-content">
                {showCustomizeTable && (
                    <CustomizeTable
                        onCancelHandler={customizeTableCancelHandler}
                        onCustomizeColumnSubmitHandler={customizeColumnSubmitHandler}
                        columns={customizeTableColumnsData}
                    />
                )}
                <DataTableWrapper
                    ref={dt}
                    columnDetails={visibleColumns}
                    header={header}
                    value={modifiedValues}
                    sortIcon={(setOrder) => <SortIcon order={setOrder} />}
                    filterIcon={FilterIcon}
                    lazy
                    paginator={true}
                    stripedRows={true}
                    rowHover={true}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    totalRecords={totalRecords}
                    emptyMessage="No details found."
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Displaying {last} records of {totalRecords} in total"
                    first={lazyState.first}
                    onSort={onSort}
                    sortField={lazyState.sortField}
                    sortOrder={lazyState.sortOrder}
                    onFilter={onFilter}
                    filters={lazyState.filters}
                    onPage={onPage}
                    rows={lazyState.rows}
                ></DataTableWrapper>
            </div>
        </>
    );
}